import React, { createContext, useContext, useEffect, useState } from 'react';
import api from '../api/axios';
import { useAuth } from './AuthContext';

const MainCompanyContext = createContext(null);

// Helper function to filter companies based on user role
const filterCompaniesByRole = (companies, adminUser) => {
  if (adminUser?.role === "A") {
    return companies; // Admin sees all companies
  } else if (adminUser?.role === "M") {
    return companies.filter(company => company.id === adminUser.maincompanyid); // Master sees only their company
  }
  return companies; // Default to all companies
};

// Helper function to handle default selection and localStorage
const setDefaultSelectedMain = (items, setSelectedMain) => {
  if (!items.length) return;

  const defaultId = items[0].id;
  setSelectedMain((prev) => {
    if (!prev) {
      try {
        localStorage.setItem('selectedMainCompany', String(defaultId));
      } catch (e) {
        console.error("Failed to save to localStorage:", e);
      }
      return defaultId;
    }
    return prev;
  });
  return defaultId;
};

// Helper function to handle initial maincompanyid setting
const handleInitialMainCompanyId = (adminUser, defaultId) => {
  if (adminUser && !adminUser.maincompanyid) {
    console.log("Initially no main company found for user, would set adminUser.maincompanyid to:", defaultId);
    // TODO: Implement API call to update user's maincompanyid
    // Example: await api.put(`/admin/users/${adminUser.id}`, { maincompanyid: defaultId });
  }
};

export function MainCompanyProvider({ children }) {
  const { adminUser } = useAuth();
  const [allMainCompanies, setAllMainCompanies] = useState([]);
  const [mainCompanies, setMainCompanies] = useState([]);
  const [selectedMain, setSelectedMain] = useState(() => {
    try {
      const saved = localStorage.getItem('selectedMainCompany');
      return saved ? Number(saved) : 0;
    } catch (e) {
      return 0;
    }
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!adminUser) return;

    const loadCompanies = async () => {
      setLoading(true);
      try {
        const { data } = await api.get('/admin/main-companies');
        const items = data?.data || [];
        setAllMainCompanies(items);
        setMainCompanies(filterCompaniesByRole(items, adminUser));

        const defaultId = setDefaultSelectedMain(items, setSelectedMain);
        handleInitialMainCompanyId(adminUser, defaultId);
      } catch (e) {
        console.error('Failed to load main companies', e?.response?.data || e?.message);
      } finally {
        setLoading(false);
      }
    };

    loadCompanies();
  }, [adminUser]);

  // Allow manual refresh from pages/components
  const loadMainCompanies = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/main-companies');
      const items = data?.data || [];
      setAllMainCompanies(items);
      setMainCompanies(filterCompaniesByRole(items, adminUser));

      const defaultId = setDefaultSelectedMain(items, setSelectedMain);
      handleInitialMainCompanyId(adminUser, defaultId);
    } catch (e) {
      console.error('Failed to refresh main companies', e?.response?.data || e?.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    try {
      if (selectedMain) {
        localStorage.setItem('selectedMainCompany', String(selectedMain));
      }
    } catch (e) {
      console.error("Failed to save selectedMain to localStorage:", e);
    }
  }, [selectedMain]);

  return (
    <MainCompanyContext.Provider value={{ mainCompanies, allMainCompanies, selectedMain, setSelectedMain, loading, loadMainCompanies }}>
      {children}
    </MainCompanyContext.Provider>
  );
}

export function useMainCompany() {
  const ctx = useContext(MainCompanyContext);
  if (!ctx) throw new Error('useMainCompany must be used within MainCompanyProvider');
  return ctx;
}

export default MainCompanyContext;
